import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import TriggerOverlay from "@/components/TriggerOverlay";
import { fetchTriggerConfig } from "@/lib/api";

const CHROMA = { chroma: "#00b140", chromapink: "#ff00ff" };

export default function TriggerView() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const cp = params.get("content_piece") || "";
  const platform = params.get("platform") || "";
  const bare = params.get("bare") === "1";
  const [bg, setBg] = useState(params.get("bg") || "preset");
  const [data, setData] = useState(null);
  const frameRef = useRef(null);

  useEffect(() => {
    fetchTriggerConfig(slug, cp, platform).then(setData).catch(() => setData(false));
  }, [slug, cp, platform]);

  const download = useCallback(async () => {
    if (!frameRef.current) return;
    const opts = { pixelRatio: 3, cacheBust: true };
    if (bg === "transparent") opts.backgroundColor = undefined;
    try {
      const url = await toPng(frameRef.current, opts);
      const a = document.createElement("a");
      a.download = `fanly-trigger-${slug}${cp ? "-" + cp : ""}${platform ? "-" + platform : ""}.png`;
      a.href = url;
      a.click();
    } catch (e) {
      toast.error("PNG export failed (an image may be blocking capture). Try bg=transparent or preset.");
    }
  }, [bg, slug, cp, platform]);

  if (data === null) {
    return <div className="min-h-[100dvh] bg-black flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#FF2A2A] animate-spin" /></div>;
  }
  if (data === false) {
    return <div className="min-h-[100dvh] bg-black flex items-center justify-center text-[#737373] font-mono-x text-sm">Trigger not found</div>;
  }

  const cfg = data.config;
  const isChroma = bg === "chroma" || bg === "chromapink";
  const frameBg = bg === "transparent" ? "transparent" : isChroma ? CHROMA[bg] : "#000";
  const transparentOverlay = bg !== "preset";

  return (
    <div className="min-h-[100dvh] bg-[#050505] flex flex-col items-center justify-center py-6"
      style={bg === "transparent" ? { backgroundImage: "linear-gradient(45deg,#161616 25%,transparent 25%),linear-gradient(-45deg,#161616 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#161616 75%),linear-gradient(-45deg,transparent 75%,#161616 75%)", backgroundSize: "24px 24px", backgroundPosition: "0 0,0 12px,12px -12px,-12px 0" } : {}}>

      {!bare && (
        <div className="w-full max-w-md px-4 mb-4 flex flex-wrap items-center gap-2" data-testid="trigger-toolbar">
          {["preset", "video", "cover", "chroma", "chromapink", "transparent"].map((b) => (
            <button key={b} data-testid={`bg-${b}`} onClick={() => setBg(b)}
              className={`font-mono-x text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors ${bg === b ? "border-[#FF2A2A] text-white" : "border-white/15 text-[#737373] hover:text-white"}`}>
              {b}
            </button>
          ))}
          <button onClick={download} data-testid="download-png"
            className="ml-auto flex items-center gap-2 bg-white text-black rounded-full px-4 py-1.5 text-xs font-semibold">
            <Download className="w-3.5 h-3.5" /> PNG
          </button>
        </div>
      )}

      {/* 9:16 frame */}
      <div
        ref={frameRef}
        data-testid="trigger-frame"
        className="relative overflow-hidden shadow-2xl"
        style={{ height: bare ? "100dvh" : "82vh", aspectRatio: "9 / 16", background: frameBg,
          borderRadius: bare ? 0 : 18 }}
      >
        {!isChroma && bg !== "transparent" && bg !== "preset" && (
          data.canvas_video ? (
            <video src={data.canvas_video} className="absolute inset-0 w-full h-full object-cover"
              autoPlay muted loop playsInline />
          ) : data.cover_image ? (
            <img src={data.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : null
        )}
        <TriggerOverlay config={cfg} transparent={transparentOverlay} />
      </div>

      {!bare && (
        <p className="mt-4 font-mono-x text-[10px] tracking-widest uppercase text-[#4d4d4d] text-center px-4">
          FANLY Trigger · {cfg.variant} · {cfg.tone_preset}{cp ? ` · ${cp}` : ""}{platform ? ` · ${platform}` : ""}<br />
          Use <span className="text-[#737373]">chroma</span> to key it into CapCut/Premiere · <span className="text-[#737373]">transparent</span> for a PNG
        </p>
      )}
    </div>
  );
}
